import { defineStore } from "pinia";
import { supabase } from "@/supabase";
import { getEffectiveUserId, isImpersonating } from "@/utils/impersonation";
import { fetchSocialProfile } from "@/api/social";

function normalizeSocialPlatformKey(platformKey) {
  const normalized = String(platformKey || "").trim().toLowerCase();
  return normalized === "twitter" ? "x" : normalized;
}

export const useSocialProfileStore = defineStore("socialProfile", {
  state: () => ({
    profile: null,
    status: "idle",
    error: "",
    loaded: false,
    loadPromise: null,
    loadingEffectiveUserId: null,
    loadingIsImpersonating: false,
    lastLoadedEffectiveUserId: null,
    isImpersonating: false,
  }),
  getters: {
    usernamesByPlatform: (state) => {
      const usernames = {};
      const socialAccounts = state.profile?.social_accounts;
      if (!socialAccounts || typeof socialAccounts !== "object") {
        return usernames;
      }

      Object.keys(socialAccounts).forEach((platformKey) => {
        const account = socialAccounts[platformKey];
        const uiPlatformKey = normalizeSocialPlatformKey(platformKey);
        if (!uiPlatformKey) return;

        let handle = "";
        if (typeof account === "string") {
          handle = String(account).trim();
        } else if (account && typeof account === "object") {
          handle = (account.display_name && String(account.display_name).trim())
            || (account.username && String(account.username).trim())
            || "";
        }

        if (handle) {
          usernames[uiPlatformKey] = handle;
        }
      });

      return usernames;
    },
  },
  actions: {
    applyBootstrapProfile(profile, session = null, { error = "" } = {}) {
      if (!session?.user?.id) {
        this.clear();
        return null;
      }

      const effectiveUserId = getEffectiveUserId(session.user.id);
      const nextIsImpersonating = isImpersonating();
      this.lastLoadedEffectiveUserId = effectiveUserId;
      this.isImpersonating = nextIsImpersonating;

      if (error) {
        this.profile = null;
        this.status = "error";
        this.error = error;
        this.loaded = false;
        return null;
      }

      this.profile = profile;
      this.status = "ready";
      this.error = "";
      this.loaded = true;
      return this.profile;
    },
    async resolveSession(session = null) {
      if (session) {
        return session;
      }
      const {
        data: { session: currentSession },
        error,
      } = await supabase.auth.getSession();
      if (error) {
        throw error;
      }
      return currentSession;
    },
    async fetchProfile(session = null, { force = false } = {}) {
      const currentSession = await this.resolveSession(session);
      if (!currentSession?.user?.id) {
        this.clear();
        return null;
      }

      const effectiveUserId = getEffectiveUserId(currentSession.user.id);
      const nextIsImpersonating = isImpersonating();
      if (
        !force
        && this.loaded
        && this.lastLoadedEffectiveUserId === effectiveUserId
        && this.isImpersonating === nextIsImpersonating
      ) {
        return this.profile;
      }
      if (
        this.loadPromise
        && this.loadingEffectiveUserId === effectiveUserId
        && this.loadingIsImpersonating === nextIsImpersonating
      ) {
        return this.loadPromise;
      }

      this.status = "loading";
      this.error = "";
      this.loadingEffectiveUserId = effectiveUserId;
      this.loadingIsImpersonating = nextIsImpersonating;

      this.loadPromise = (async () => {
        const payload = await fetchSocialProfile(currentSession);
        if (!payload) {
          this.profile = null;
          this.loaded = true;
          this.lastLoadedEffectiveUserId = effectiveUserId;
          this.isImpersonating = nextIsImpersonating;
          this.status = "ready";
          this.error = "";
          return null;
        }

        this.profile = payload?.profile && typeof payload.profile === "object"
          ? payload.profile
          : null;
        this.loaded = true;
        this.lastLoadedEffectiveUserId = effectiveUserId;
        this.isImpersonating = nextIsImpersonating;
        this.status = "ready";
        this.error = "";
        return this.profile;
      })().catch((error) => {
        this.status = "error";
        this.error = error?.message || String(error);
        throw error;
      }).finally(() => {
        this.loadPromise = null;
        this.loadingEffectiveUserId = null;
        this.loadingIsImpersonating = false;
      });

      return this.loadPromise;
    },
    async refreshProfile(session = null) {
      return this.fetchProfile(session, { force: true });
    },
    clear() {
      this.profile = null;
      this.status = "idle";
      this.error = "";
      this.loaded = false;
      this.loadPromise = null;
      this.loadingEffectiveUserId = null;
      this.loadingIsImpersonating = false;
      this.lastLoadedEffectiveUserId = null;
      this.isImpersonating = false;
    },
  },
});
